import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError } from '@/api/client';
import { useCreateCustomer, useUpdateCustomer } from '@/api/customers';
import type { Customer } from '@/api/types';
import { Button, Input, Modal, Select, Textarea, useToast } from '@/components/ui';
import { humanize } from '@/lib/format';
import { CUSTOMER_TYPES } from './meta';

const optional = (s: z.ZodString) => z.union([z.literal(''), s]);
const schema = z.object({
  name: z.string().trim().min(1, 'Required').max(120),
  email: optional(z.string().trim().email('Enter a valid email')),
  phone: optional(
    z
      .string()
      .trim()
      .regex(/^\+?[0-9 ()-]{6,20}$/, 'Enter a valid phone number'),
  ),
  type: z.enum(['creator', 'business', 'brand', 'other']),
  plan: z.string().trim().max(40),
  city: z.string().trim().max(80),
  kollabrUserId: z.string().trim().max(64),
  tags: z.string(),
  notes: z.string().max(5000),
});
type FormValues = z.infer<typeof schema>;

export function CustomerFormModal({
  customer,
  onClose,
  onCreated,
}: {
  customer: Customer | null;
  onClose: () => void;
  onCreated?: (c: Customer) => void;
}) {
  const toast = useToast();
  const create = useCreateCustomer();
  const update = useUpdateCustomer();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: customer?.name ?? '',
      email: customer?.email ?? '',
      phone: customer?.phone ?? '',
      type: customer?.type ?? 'creator',
      plan: customer?.plan ?? '',
      city: customer?.city ?? '',
      kollabrUserId: customer?.kollabrUserId ?? '',
      tags: customer?.tags.join(', ') ?? '',
      notes: customer?.notes ?? '',
    },
  });

  const onError = (e: Error) => {
    if (e instanceof ApiError && e.status === 409) setError('email', { message: e.message });
    else if (e instanceof ApiError && Object.keys(e.fieldErrors).length) {
      for (const [path, message] of Object.entries(e.fieldErrors))
        setError(path as keyof FormValues, { message });
    } else toast.error(e.message);
  };

  const onSubmit = handleSubmit((v) => {
    const input = {
      name: v.name,
      email: v.email || null,
      phone: v.phone || null,
      type: v.type,
      plan: v.plan || null,
      city: v.city || null,
      kollabrUserId: v.kollabrUserId || null,
      tags: [
        ...new Set(
          v.tags
            .split(/[\s,]+/)
            .map((t) => t.trim().toLowerCase())
            .filter(Boolean),
        ),
      ],
      notes: v.notes,
    };
    if (customer) {
      update.mutate(
        { id: customer.id, input },
        { onSuccess: () => (toast.success('Customer saved'), onClose()), onError },
      );
    } else {
      create.mutate(input, {
        onSuccess: (c) => {
          toast.success('Customer added');
          onClose();
          onCreated?.(c);
        },
        onError,
      });
    }
  });

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={customer ? `Edit ${customer.name}` : 'New customer'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="customer-form" loading={create.isPending || update.isPending}>
            {customer ? 'Save' : 'Add customer'}
          </Button>
        </>
      }
    >
      <form id="customer-form" onSubmit={onSubmit} noValidate className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Name" required error={errors.name?.message} {...register('name')} />
          <Select label="Type" {...register('type')}>
            {CUSTOMER_TYPES.map((t) => (
              <option key={t} value={t}>
                {humanize(t)}
              </option>
            ))}
          </Select>
          <Input label="Email" type="email" error={errors.email?.message} {...register('email')} />
          <Input
            label="Phone"
            type="tel"
            placeholder="+91 98765 43210"
            error={errors.phone?.message}
            {...register('phone')}
          />
          <Input
            label="Plan"
            placeholder="free, pro, business…"
            error={errors.plan?.message}
            {...register('plan')}
          />
          <Input label="City" error={errors.city?.message} {...register('city')} />
          <Input
            label="Platform user ID"
            hint="Links this customer to their account on your platform"
            error={errors.kollabrUserId?.message}
            {...register('kollabrUserId')}
          />
          <Input
            label="Tags"
            placeholder="vip, fashion"
            hint="Comma separated"
            error={errors.tags?.message}
            {...register('tags')}
          />
        </div>
        <Textarea
          label="Notes"
          rows={3}
          hint="Visible to the support team only"
          error={errors.notes?.message}
          {...register('notes')}
        />
      </form>
    </Modal>
  );
}
